import OrderProcessingView, {
  OrderProcessStatus
} from "@saleor/apps/components/OrderProcessing";
import { useUser } from "@saleor/auth";
import { showNotifyMessage } from "@saleor/changeOrder/utils";
import Container from "@saleor/components/Container";
import DialogConfirmError from "@saleor/components/DialogConfirmError";
import MyTypography from "@saleor/components/MyTypography";
import PageHeader from "@saleor/components/PageHeader";
import TimeOutPage from "@saleor/components/TimeOutPage";
import { STAFF_UPDATE } from "@saleor/config";
import DrawAddProduct from "@saleor/createOrder/components/DrawAddProduct";
import { previewOrderPath } from "@saleor/createOrder/urls";
import ProductDetails from "@saleor/createOrderCustomer/components/ProductDetails";
import EmptyProduct from "@saleor/createOrderCustomer/components/ProductDetails/EmptyProduct";
import DialogError from "@saleor/createOrderExport/components/DialogError";
import {
  CustomerOrderLinesUpdateInput,
  ScgOrderStatus,
  UpdateCustomerOrderLinesMutationVariables,
  UpdateCustomerOrderMutationVariables,
  useCustomerCartTotalsQuery,
  useCustomerOrderHeaderQuery,
  useCustomerOrderQuery,
  useDeleteCustomerOrderLinesMutation,
  useUpdateCustomerOrderLinesMutation,
  useUpdateCustomerOrderMutation
} from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginationLocal from "@saleor/hooks/usePaginationLocal";
import { ListViews, Pagination } from "@saleor/types";
import { getPreviousDate, parseDateString } from "@saleor/utils/date";
import { mapEdgesToItems } from "@saleor/utils/maps";
import { useFormik } from "formik";
import lodash_find from "lodash/find";
import moment from "moment/moment";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import * as Yup from "yup";

import CustomerInfo from "./../components/CollectionForm";
import CreateOrderFooter from "./../components/CreateOrderFooter";

// import { orderCustomerProcessingFailed } from "../urls";

interface CreateOrderViewProps {
  id: string;
  params: Pagination & { enabledCreate?: string };
}

export interface ValidateFormProps {
  isCheckValidate: boolean;
  isValidate: {
    validateInfo: boolean;
    validateProduct: boolean;
  };
}

export interface CustomerDataProps {
  orderDate: any | null;
  orderNo: string | null;
  shipTo: string | null;
  billTo: string | null;
  requestDeliveryDate: any | null;
  remarkForLogistic: string | null;
  orderStatus: string | null;
  unloadingPoint: string | null;
  internalCommentsToWarehouse: string | null;
  internalCommentsToLogistic: string | null;
  contract: any;
  customerInfo: any;
  soldToAddress: string;
}

let timer: NodeJS.Timer | null = null;

enum EnumButtonType {
  Confirm = "Confirm",
  ApplyAll = "ApplyAll"
}

const CreateOrderView: React.FC<CreateOrderViewProps> = ({ id, params }) => {
  const intl = useIntl();
  const notify = useNotifier();
  const { soldToId } = useUser();
  const [listLineState, setListLineState] = useState([]);
  const [dataFormCustomer, setDataFormCustomer] = useState<any>({});
  const [orderId, setOrderId] = useState<string>("");
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [, dispatchAppState] = useAppState();
  const [buttonType, setButtonType] = useState<EnumButtonType>(
    EnumButtonType.ApplyAll
  );
  const [componentKey, setComponentKey] = useState<number>(0);
  const [orderProcessStatus, setOrderProcessStatus] = useState<
    OrderProcessStatus
  >(OrderProcessStatus.inconclusive);
  const [orderProcessError, setOrderProcessError] = useState("");

  const [dialogError, setDialogError] = useState(false);
  const [errorSource, setErrorSource] = useState("");
  const [errorOrders, setErrorOrders] = useState([]);
  const [errorItems, setErrorItems] = useState([]);

  const [openError, setOpenError] = useState<boolean>(false);
  const [popUpContent, setPopUpContent] = useState(null);
  const navigate = useNavigator();
  const paginationNumber = 50;

  const [validateForm, setValidateForm] = useState<ValidateFormProps>({
    isCheckValidate: false,
    isValidate: {
      validateInfo: false,
      validateProduct: false
    }
  });

  if (params.enabledCreate !== "true") {
    return (
      <OrderProcessingView
        orderProcessStatus={OrderProcessStatus.block}
        onFailed={null}
      />
    );
  }

  const { updateListSettings, settings } = useListSettings(
    ListViews.CREATE_ORDER_PRODUCT_LIST
  );

  useEffect(() => {
    updateListSettings("rowNumber", paginationNumber);
  }, []);

  const { data: dataCustomerOrderHeader } = useCustomerOrderHeaderQuery({
    skip: !id,
    variables: {
      orderId: id
    }
  });

  const {
    data: dataCustomerOrder,
    refetch: refetchCustomerOrder,
    loading: loadingCustomerOrder
  } = useCustomerOrderQuery({
    variables: {
      orderId: id,
      first: 100
    },
    skip: !id,
    fetchPolicy: "cache-and-network"
  });

  const taxPercent = useMemo(
    () => dataCustomerOrder?.customerOrder?.taxPercent || 0,
    [dataCustomerOrder]
  );

  const {
    currentData,
    next,
    prev,
    disableNext,
    disablePrev
  } = usePaginationLocal(listLineState, settings.rowNumber);

  const handleNextPage = () => {
    next();
    setComponentKey(prev => prev + 1);
  };

  const handlePrevPage = () => {
    prev();
    setComponentKey(prev => prev + 1);
  };

  const [
    handleSaveLine,
    { loading: loadingApplyAll }
  ] = useUpdateCustomerOrderLinesMutation({
    onCompleted: () => {
      refetchCustomerOrder();
    }
  });

  useEffect(() => {
    if (dataCustomerOrder && !loadingCustomerOrder) {
      const edgeNode = mapEdgesToItems(dataCustomerOrder.customerOrder?.lines);
      const listLine = edgeNode.map(item => ({
        ...item,
        errorQuantity: false,
        errorDate: false,
        writable: true
      }));

      setListLineState(prev => {
        if (listLine.length > prev.length) {
          const newListLine = listLine.map(line => {
            const result = lodash_find(prev, { id: line.id });

            if (result) {
              return {
                ...result,
                requestDeliveryDate:
                  result.requestDeliveryDate || line.requestDeliveryDate
              };
            } else {
              return line;
            }
          });
          return newListLine;
        } else {
          return listLine;
        }
      });
      setComponentKey(componentKey + 1);
    }
  }, [dataCustomerOrder, loadingCustomerOrder]);

  useEffect(() => {
    clearTimeout(timer);
  }, []);

  const [
    deleteCustomerOrder,
    { loading: loadingDelete }
  ] = useDeleteCustomerOrderLinesMutation({
    onCompleted: () => {
      // refetchCustomerOrder();
    }
  });

  const refetchCustomerOrderData = () => {
    refetchCustomerOrder();
  };

  const deleteCustomerLines = list => {
    deleteCustomerOrder({
      variables: {
        deleteAll: false,
        orderId: id,
        ids: list
      }
    }).then(() => {
      refetchCustomerOrder();
      const listVariantAfterDeleted = listLineState.filter(
        line => !list.includes(line.id)
      );
      listVariantAfterDeleted.forEach(line => {
        const remaining = line.contractProduct?.remainingQuantity || 0;
        line.errorQuantity =
          !line.quantity ||
          getTotalQuantityGroupByVariant(listVariantAfterDeleted, line) >
            remaining;
      });
      setListLineState(listVariantAfterDeleted);
      setComponentKey(prev => prev + 1);
    });
  };

  const getTotalQuantityGroupByVariant = (listOrderLines, line) => {
    let total = 0;
    listOrderLines.forEach(item => {
      if (item.contractProduct?.id === line.contractProduct?.id) {
        total += Number(item.weightPerUnit) * (item.quantity || 0);
      }
    });
    return total;
  };

  useCustomerCartTotalsQuery({
    fetchPolicy: "no-cache",
    skip: !orderId,
    variables: {
      soldToId
    },
    onCompleted: cartInfoData => {
      dispatchAppState({
        payload: {
          orderCount: cartInfoData?.customerCartTotals?.totalProducts || 0
        },
        type: "setOrderCount"
      });
      navigate(previewOrderPath(orderId));
    }
  });

  const [
    handleUpdateCustomerInfo,
    { loading: loadingUpdateCustomerInfo }
  ] = useUpdateCustomerOrderMutation({
    onCompleted: data => {
      if (buttonType === EnumButtonType.Confirm) {
        if (data?.updateCustomerOrder?.success) {
          setOrderId(data?.updateCustomerOrder?.order?.id);
          showNotifyMessage(
            notify,
            [],
            [],
            [],
            [],
            data?.updateCustomerOrder?.warningMessages
          );

          if (data?.updateCustomerOrder?.iPlanMessages?.length > 0) {
            const iplanErrList = data?.updateCustomerOrder?.iPlanMessages?.map(
              item => `${item.firstCode}${item.secondCode} - ${item.message}`
            );

            notify({
              status: "error",
              sourceSystem: "IPLAN",
              apiMessage: iplanErrList.join("\n")
            });
          }

          return;
        } else {
          setOrderProcessStatus(OrderProcessStatus.inconclusive);

          if (
            data?.updateCustomerOrder?.sapOrderMessages?.length > 0 ||
            data?.updateCustomerOrder?.sapItemMessages?.length > 0
          ) {
            const sapErrorOrders = data?.updateCustomerOrder?.sapOrderMessages?.map(
              ({ errorCode, errorMessage }) => `${errorCode} - ${errorMessage}`
            );

            const sapErrorItems = data?.updateCustomerOrder?.sapItemMessages?.map(
              ({ itemNo, errorCode, errorMessage }) => ({
                itemNo,
                itemCode: errorCode,
                itemStatus: errorMessage
              })
            );

            setDialogError(true);
            setErrorSource("SAP");
            setErrorOrders(sapErrorOrders);
            setErrorItems(sapErrorItems);
          } else if (data?.updateCustomerOrder?.iPlanMessages?.length > 0) {
            const iplanErrorItems = data?.updateCustomerOrder?.iPlanMessages?.map(
              item => ({
                itemNo: item.itemNo,
                itemCode: `${item.firstCode}${item.secondCode}`,
                itemStatus: item.message
              })
            );

            setDialogError(true);
            setErrorSource("IPLAN");
            setErrorItems(iplanErrorItems);
          } else {
            setOrderProcessStatus(OrderProcessStatus.failed);
            let sapOrderErrors = "";
            data?.updateCustomerOrder?.errors?.forEach(({ message }) => {
              sapOrderErrors += `${message} \n`;
            });
            setOrderProcessError(sapOrderErrors);
          }
        }
      }
    },
    onError: error => {
      if (error?.message?.includes("Read timed out")) {
        setOrderProcessStatus(OrderProcessStatus.timeOut);
        return;
      }
      if (buttonType === EnumButtonType.Confirm) {
        setOrderProcessStatus(OrderProcessStatus.failed);

        if (error.message === "Network request failed") {
          setOrderProcessError("400");
        } else {
          setOrderProcessError(error.message);
        }
      }
    }
  });

  const getPriceData = () => {
    let totalPrice = 0;
    listLineState.forEach(line => {
      if (line.quantity) {
        totalPrice +=
          line.quantity *
          line?.contractProduct?.pricePerUnit *
          line?.weightPerUnit;
      }
    });
    const vat = totalPrice * taxPercent;
    const netPrice = totalPrice + vat;
    return {
      netPrice,
      vat,
      totalPrice
    };
  };

  const priceData = {
    itemCount: dataCustomerOrder?.customerOrder?.lines?.totalCount,
    totalPrice: getPriceData().totalPrice,
    vat: getPriceData().vat,
    netPrice: getPriceData().netPrice,
    usd: dataCustomerOrder?.customerOrder?.contract?.currency
  };

  const submitFormCustomer = async data => {
    setButtonType(EnumButtonType.ApplyAll);
    const paramsInfo: UpdateCustomerOrderMutationVariables = {
      id,
      input: {
        billTo: data.billTo,
        orderDate: data.orderDate,
        orderNo: data.orderNo,
        requestDeliveryDate: data.requestDeliveryDate,
        shipTo: data.shipTo,
        unloadingPoint: data.unloadingPoint,
        internalCommentsToWarehouse: data.internalCommentsToWarehouse,
        internalCommentsToLogistic: data.internalCommentsToLogistic,
        remarkForInvoice: data.remarkForInvoice,
        remarkForLogistic: data.remarkForLogistic,
        confirm: false
      }
    };

    const params: UpdateCustomerOrderLinesMutationVariables = {
      orderId: id,
      input: {
        lines: [...listLineState].map(item => ({
          id: item.id,
          quantity: item.quantity,
          requestDeliveryDate: data.requestDeliveryDate
        })),
        requestDeliveryDate: data.requestDeliveryDate,
        applyAll: true
      }
    };
    await setDataFormCustomer(data);
    // Save customer info
    await handleUpdateCustomerInfo({
      variables: paramsInfo
    });

    // Apply all info for lines
    handleSaveLine({
      variables: params
    });
  };

  const handleCreateOrder = async () => {
    await setButtonType(EnumButtonType.Confirm);
    // submit
    await handleCheckValidateForm();
    const checkValidateForm = Object.keys(validateForm.isValidate)
      .map(key => validateForm.isValidate[key])
      .filter(ck => ck);
    // Pass isValidate[key] === true

    const params: UpdateCustomerOrderMutationVariables = {
      id,
      input: {
        billTo: dataFormCustomer.billTo,
        orderDate: dataFormCustomer.orderDate,
        orderNo: dataFormCustomer.orderNo,
        requestDeliveryDate: dataFormCustomer.requestDeliveryDate,
        shipTo: dataFormCustomer.shipTo,
        unloadingPoint: dataFormCustomer.unloadingPoint,
        internalCommentsToWarehouse:
          dataFormCustomer.internalCommentsToWarehouse,
        internalCommentsToLogistic: dataFormCustomer.internalCommentsToLogistic,
        remarkForInvoice: dataFormCustomer.remarkForInvoice,
        remarkForLogistic: dataFormCustomer.remarkForLogistic,
        confirm: true
      }
    };
    const paramInput: CustomerOrderLinesUpdateInput = {
      lines: [...listLineState].map(item => ({
        id: item.id,
        quantity: item.quantity,
        requestDeliveryDate: item.requestDeliveryDate
      }))
    };

    if (!dataFormCustomer.requestDeliveryDate && !dataFormCustomer.orderNo) {
      await customerFormik.setFieldError("requestDeliveryDate", "Required");
      await customerFormik.setFieldTouched("requestDeliveryDate", true);
      await customerFormik.setFieldError("orderNo", "Required");
    } else if (!dataFormCustomer.orderNo) {
      await customerFormik.setFieldError("orderNo", "Required");
    } else if (!dataFormCustomer.requestDeliveryDate) {
      await customerFormik.setFieldError("requestDeliveryDate", "Required");
      await customerFormik.setFieldTouched("requestDeliveryDate", true);
    }

    if (
      checkValidateForm.length === 2 &&
      dataFormCustomer.orderNo &&
      dataFormCustomer.requestDeliveryDate
    ) {
      await handleSaveLine({
        variables: {
          orderId: id,
          input: paramInput
        }
      });
      handleUpdateCustomerInfo({
        variables: params
      });
      setOrderProcessStatus(OrderProcessStatus.success);
    }
  };

  const handleGetCustomerInfo = values => {
    setDataFormCustomer(values);
  };
  const customerInfoData = useMemo(() => {
    const customerData: CustomerDataProps = {
      soldToAddress: dataCustomerOrderHeader?.customerOrder?.soldToAddress,
      billTo: dataCustomerOrderHeader?.customerOrder?.billTo,
      shipTo: dataCustomerOrderHeader?.customerOrder?.shipTo,
      orderDate: dataCustomerOrderHeader?.customerOrder?.orderDate,
      orderNo: dataCustomerOrderHeader?.customerOrder?.orderNo,
      remarkForLogistic:
        dataCustomerOrderHeader?.customerOrder?.remarkForLogistic,
      requestDeliveryDate:
        dataCustomerOrderHeader?.customerOrder?.requestDeliveryDate ||
        undefined,
      orderStatus: dataCustomerOrderHeader?.customerOrder?.status,
      unloadingPoint: dataCustomerOrderHeader?.customerOrder?.unloadingPoint,
      internalCommentsToWarehouse:
        dataCustomerOrderHeader?.customerOrder?.internalCommentsToWarehouse,
      internalCommentsToLogistic:
        dataCustomerOrderHeader?.customerOrder?.internalCommentsToLogistic,
      contract: dataCustomerOrderHeader?.customerOrder?.contract,
      customerInfo: dataCustomerOrderHeader?.customerOrder?.createdBy
    };
    return customerData;
  }, [dataCustomerOrderHeader?.customerOrder]);

  const hasValidationError = useMemo(() => {
    // Validate requestDate value should in furture
    const requestDateValue = dataFormCustomer?.requestDeliveryDate;
    const checkRequestDateValueinLines = listLineState.filter(
      ck =>
        ck.requestDeliveryDate &&
        moment(ck.requestDeliveryDate).set("h", 1) < moment().set("h", 0)
    );

    return (
      listLineState?.length < 1 ||
      checkRequestDateValueinLines.length > 0 ||
      (requestDateValue && new Date(requestDateValue) <= getPreviousDate())
    );
  }, [listLineState, dataFormCustomer?.requestDeliveryDate]);

  const handleOpenPopup = () => {
    setIsPopupOpen(true);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
  };

  const handleCheckValidateForm = () => {
    setValidateForm({ ...validateForm, isCheckValidate: true });
  };

  useEffect(() => {
    if (validateForm.isCheckValidate) {
      timer = setTimeout(
        () => setValidateForm({ ...validateForm, isCheckValidate: false }),
        200
      );
      return () => clearTimeout(timer);
    }
  }, [validateForm.isCheckValidate]);

  // Loading system
  useEffect(() => {
    const loading =
      loadingCustomerOrder ||
      loadingUpdateCustomerInfo ||
      loadingApplyAll ||
      loadingDelete;
    dispatchAppState({
      payload: {
        value: loading
      },
      type: "displayLoader"
    });
  }, [loadingCustomerOrder, loadingUpdateCustomerInfo, loadingDelete]);

  const getErrorLine = () =>
    listLineState.some(item => item.errorQuantity || item.errorDate);

  const handleProcessCreateOrderFailed = () => {
    setOrderProcessStatus(OrderProcessStatus.inconclusive);
    customerFormik.resetForm();
  };

  // Customer info formik
  const CreateOrderSchema = Yup.object().shape({
    shipTo: Yup.string().required("Required"),
    billTo: Yup.string().required("Required"),
    unloadingPoint: Yup.string(),
    orderNo: Yup.string(),
    orderDate: Yup.date().required("Required"),
    requestDeliveryDate: Yup.date()
      .transform(parseDateString)
      .min(
        getPreviousDate(),
        "กรุณาเปลี่ยน “Request Date” (ไม่สามารถใส่วันที่ผ่านมาแล้วได้)"
      )
      .required("Required")
  });

  const customerFormik = useFormik({
    initialValues: {
      shipTo: "",
      billTo: "",
      unloadingPoint: "",
      internalCommentsToWarehouse: "",
      internalCommentsToLogistic: "",
      orderNo: "",
      orderDate: customerInfoData?.orderDate || new Date().toDateString(),
      requestDeliveryDate: customerInfoData?.requestDeliveryDate,
      remarkForLogistic: ""
    },
    onSubmit: () => {
      submitFormCustomer(customerFormik.values);
    },
    validationSchema: CreateOrderSchema
  });

  if (orderProcessStatus === OrderProcessStatus.timeOut) {
    return (
      <TimeOutPage
        timeOutText={
          <>
            <MyTypography variant={"body2"}>
              กรุณาตรวจสอบคำสั่งซื้อที่เมนู Inquiry - Order Confirmation
            </MyTypography>
            <MyTypography variant={"body2"}>
              หากไม่พบคำสั่งซึ้อ กรุณาติดต่อ Customer Services: (02-586-5555 กด
              11)
            </MyTypography>
          </>
        }
      />
    );
  }

  return (
    <>
      {orderProcessStatus === OrderProcessStatus.inconclusive && (
        <Container>
          <PageHeader
            title={intl.formatMessage({ defaultMessage: "Create Order" })}
          />
          <MyTypography variant="h7">
            {intl.formatMessage({ defaultMessage: "สร้างคำสั่งซื้อของฉัน" })}
          </MyTypography>
          {customerInfoData && (
            <CustomerInfo
              customerFormik={customerFormik}
              customerData={customerInfoData}
              submitForm={submitFormCustomer}
              handleGetCustomerInfo={handleGetCustomerInfo}
              validateForm={validateForm}
              setValidateForm={setValidateForm}
            />
          )}
          {listLineState.length ? (
            <ProductDetails
              key={componentKey}
              currency={
                dataCustomerOrder?.customerOrder?.contract?.currency || ""
              }
              componentKey={componentKey}
              dataOrder={dataCustomerOrder}
              listLineAll={listLineState}
              listLineCurrent={currentData()}
              setListLine={setListLineState}
              validateForm={validateForm}
              setValidateForm={setValidateForm}
              disabled={
                loadingCustomerOrder ||
                (customerInfoData?.orderStatus || "").toUpperCase() ===
                  ScgOrderStatus.CONFIRMED
              }
              onNextPage={handleNextPage}
              onPreviousPage={handlePrevPage}
              onRowClick={() => null}
              hasNextPage={!disableNext()}
              hasPreviousPage={!disablePrev()}
              settings={settings}
              onUpdateListSettings={updateListSettings}
              deleteOrderLine={deleteCustomerLines}
            />
          ) : (
            <EmptyProduct />
          )}
          <CreateOrderFooter
            taxPercent={taxPercent}
            onOrder={handleCreateOrder}
            priceData={priceData}
            disabled={
              (customerInfoData?.orderStatus || "").toUpperCase() ===
                ScgOrderStatus.CONFIRMED ||
              loadingCustomerOrder ||
              loadingUpdateCustomerInfo ||
              loadingApplyAll ||
              loadingDelete ||
              getErrorLine()
            }
            disabledAddMore={
              (customerInfoData?.orderStatus || "").toUpperCase() ===
                ScgOrderStatus.CONFIRMED ||
              loadingCustomerOrder ||
              loadingUpdateCustomerInfo ||
              loadingApplyAll ||
              loadingDelete
            }
            hasValidationError={hasValidationError}
            onPopup={handleOpenPopup}
          />

          <DrawAddProduct
            cartData={listLineState}
            open={isPopupOpen}
            onClose={handleClosePopup}
            contractId={dataCustomerOrder?.customerOrder?.contract?.id}
            cartId={id}
            refetchData={refetchCustomerOrderData}
            staffUpdate={STAFF_UPDATE.UPDATE_ORDER}
            title={intl.formatMessage({ defaultMessage: "เพิ่มรายการสินค้า" })}
            subTitle={intl.formatMessage({
              defaultMessage: "คำสั่งซื้อของฉัน"
            })}
            isCustomerCreateFlow={true}
            setVisibleDialogError={setOpenError}
            setPopUpContent={setPopUpContent}
          />

          <DialogConfirmError
            open={dialogError}
            onConfirm={() => setDialogError(false)}
            onCancel={() => setDialogError(false)}
            errorSource={errorSource}
            errorOrders={errorOrders}
            errorItems={errorItems}
          />
        </Container>
      )}
      <OrderProcessingView
        isCreateOrder
        orderProcessStatus={orderProcessStatus}
        onFailed={handleProcessCreateOrderFailed}
        errorCode={orderProcessError}
      />
      <DialogError
        onClose={() => {
          setOpenError(false);
          setPopUpContent(null);
        }}
        open={openError}
        body={popUpContent}
      />
    </>
  );
};

export default CreateOrderView;
